import React, {lazy, ReactElement, Suspense, useCallback, useMemo, useReducer, useState} from 'react';
import ReactDOM from 'react-dom';
import {connectedDomNodesReducer} from '../../lib/reducer/connectedDomNodesReducer';
import useNonInitialEffect from '../../lib/hooks/useNonInitialEffect';
import {TPlotData} from './getDataForHighcharts';

type TChartNode = {
  domNode: TDomNode;
  legacyBridge: TLegacyBridge;
  updateKey: string;
};

type TLegacyBridge = {
  plotData: TPlotData;
  plotID: string;
  chartTitle?: string;
  chartHeight?: number;
  chartMulticolor?: boolean;
  hasWhiteLabel?: boolean;
  isBackend?: boolean;
};

type TDomNode = HTMLElement & {
  legacyBridge?: TLegacyBridge;
};

// Highcharts is only loaded once the first chart is mounted
const LiveResultsChart = lazy(() => import('../../components/LiveResultsChart/LiveResultsChart').then((module) => ({default: module.LiveResultsChart})));

const createNodes = (domNode: TDomNode): TChartNode => {
  // Create a unique key based on plotID and legacyBridge data to force updates
  const bridge = domNode.legacyBridge!;
  const updateKey = `${bridge.plotID}-${bridge.chartMulticolor}-${bridge.chartHeight}-${Date.now()}`;

  return {
    domNode,
    legacyBridge: bridge,
    updateKey,
  };
};

export const ChartPortals = (): ReactElement => {
  const [domNodes, addDomNode] = useReducer(connectedDomNodesReducer, []);
  const [nodes, setNodes] = useState<TChartNode[]>([]);
  // Memoize the callback to prevent re-creating it on every render
  const addReactChart = useCallback((domNode: TDomNode, legacyBridge: TLegacyBridge): void => {
    domNode.legacyBridge = legacyBridge;
    addDomNode(domNode);
  }, []);

  // Set up window.reactPortals once
  if (!window.reactPortals) {
    window.reactPortals = {};
  }
  window.reactPortals.addReactChart = addReactChart;

  useNonInitialEffect(() => {
    setNodes(domNodes.map((domNode: TDomNode) => createNodes(domNode)));
  }, [domNodes]);

  // Memoize nodes rendering to avoid re-calculating on every render
  const portals = useMemo(() => {
    return nodes.map((node) => ReactDOM.createPortal(<LiveResultsChart key={node.updateKey} node={node} />, node.domNode, `chart-portal-${node.legacyBridge.plotID}-${node.updateKey}`));
  }, [nodes]);

  return <Suspense fallback={null}>{portals}</Suspense>;
};
